import Auth from 'services/authService'
import { IAgentRun } from '../model/types'

export const AGENT_API = process.env.REACT_APP_AGENT_API_ENDPOINT

export class AgentRequestError extends Error {
  constructor(message: string, readonly status?: number) {
    super(message)
    this.name = 'AgentRequestError'
    Object.setPrototypeOf(this, AgentRequestError.prototype)
  }
}

// A missing status means preparation failed before fetch. Timeout/conflict/server
// responses remain uncertain because they can follow a run that already started.
export function isRejectedAgentRequest(error: unknown) {
  return (
    error instanceof AgentRequestError &&
    (error.status === undefined || [400, 401, 403, 404, 413, 422, 429].includes(error.status))
  )
}

// Mutations are deliberately never replayed: a failed connection can hide a successful run.
export async function agentRequest(path: string, init: RequestInit = {}) {
  if (!AGENT_API) throw new AgentRequestError('Agent API is not configured.')
  let idToken: string
  try {
    const tokens = await Auth.getTokens()
    idToken = tokens.idToken
  } catch {
    throw new AgentRequestError('Agent could not prepare your authentication. Try sending again.')
  }
  const response = await fetch(`${AGENT_API}${path}`, {
    ...init,
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json', ...init.headers },
  })
  if (!response.ok) throw new AgentRequestError(`Request failed (HTTP ${response.status}).`, response.status)
  return response
}

export async function getRuns(agentId: string, before?: string, signal?: AbortSignal): Promise<IAgentRun[]> {
  const response = await agentRequest(
    `/agents/${agentId}/runs?limit=20${before ? `&before=${encodeURIComponent(before)}` : ''}`,
    { signal }
  )
  const data = await response.json()
  if (
    !Array.isArray(data) ||
    data.some(
      (run) =>
        typeof run.id !== 'string' ||
        typeof run.started_at !== 'string' ||
        (run.steps != null && !Array.isArray(run.steps))
    )
  ) {
    throw new Error('Agent returned an invalid history response.')
  }
  return data
}

export interface IAgentFeedback {
  id: string
  agent_run_id: string
  source: string
  status: string
  created_at: string
}

// Reports a wrong answer for one run (PRD-1). The server is idempotent per run.
export async function reportAgentRun(
  agentId: string,
  runId: string,
  description: string,
  signal?: AbortSignal
): Promise<IAgentFeedback> {
  const response = await agentRequest(`/agents/${agentId}/runs/${runId}/feedback`, {
    method: 'POST',
    body: JSON.stringify({ description }),
    signal,
  })
  const data = await response.json()
  if (!data || typeof data.id !== 'string' || typeof data.agent_run_id !== 'string')
    throw new Error('Agent returned an invalid feedback response.')
  return data
}

export interface IAgentEvent {
  event: string
  step?: number
  delta?: string
  content?: string
  message?: string
  tool_name?: string
  name?: string
  run_id?: string
  status?: string
}

export async function readAgentStream(response: Response, onEvent: (event: IAgentEvent) => void) {
  if (!response.body) throw new Error('The response stream is unavailable. Check history before sending again.')
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let data: string[] = []
  const dispatch = () => {
    if (!data.length) return
    const payload = data.join('\n')
    data = []
    if (payload === '[DONE]') return
    const event = JSON.parse(payload)
    if (event && typeof event.event === 'string') onEvent(event)
  }
  const line = (value: string) => {
    if (!value) dispatch()
    else if (value.startsWith('data:')) data.push(value.slice(5).replace(/^ /, ''))
  }
  try {
    let finished = false
    while (!finished) {
      const chunk = await reader.read()
      buffer += decoder.decode(chunk.value, { stream: !chunk.done })
      let end: number
      while ((end = buffer.indexOf('\n')) >= 0) {
        line(buffer.slice(0, end).replace(/\r$/, ''))
        buffer = buffer.slice(end + 1)
      }
      finished = chunk.done
    }
    if (buffer) line(buffer.replace(/\r$/, ''))
    dispatch()
  } finally {
    reader.releaseLock()
  }
}
